/**!SECTION
 * @file ClientInput.js
 * @version 1.0.0
 * @name ClientInput
 * @namespace Fields
 * @requires react-admin
 * @requires react
 * @requires @mui/material
 * @desc An Autocomplete field that can be used to search for and select one or more clients
 * 
 * @param {function} valueHandler - An optional function that can be called when the selected Clients changes
 * @param {function} optionsFilter - An optional function that cam be called to filter or transform the options returned from the API
 * 
 */

import { useState, useCallback, useRef } from "react";
import { useGetList } from "react-admin";
import AutoComplete from "@mui/material/Autocomplete";
import TextField from "@mui/material/TextField";
import CircularProgress from "@mui/material/CircularProgress";
import debounce from "lodash/debounce";

const ClientInput = ({ valueHandler = (updatedValue) => {}, optionsFilter = (options) => options, defaultValue = [] }) => {
	const [selectedClients, setSelectedClients] = useState(defaultValue);
	const [clientQuery, setClientQuery] = useState("");
	const [inputValue, setInputValue] = useState("");

	const debouncedSetQuery = useRef(
		debounce((value) => setClientQuery(value), 300)
	).current;

	const filter = {};
	if (clientQuery) {
		const parts = clientQuery.trim().split(/\s+/);
		if (parts.length >= 2) {
			filter.first_name = { $containsi: parts[0] };
			filter.last_name = { $containsi: parts.slice(1).join(" ") };
		} else {
			filter.q = clientQuery;
		}
	}

	const { data: clients, isLoading: clientsLoading } = useGetList("clients", {filter}, {
		keepPreviousData: true,
	});

	return (
		<AutoComplete
			name="client_input"
			multiple
			options={clients ? optionsFilter(clients) : []}
			getOptionLabel={(option) => `${option.first_name} ${option.last_name}`}
			value={selectedClients}
			inputValue={inputValue}
			noOptionsText={"Start typing to search for clients"}
			onInputChange={(event, newInputValue, reason) => {
				if (reason === "reset") return;
				setInputValue(newInputValue);
				debouncedSetQuery(newInputValue);
			}}
			onChange={(event, newValue) => {
				setSelectedClients(newValue);
				valueHandler(newValue);
			}}
			isOptionEqualToValue={(option, value) => option.id === value.id}
			loading={clientsLoading}
			renderInput={(params) => (
				<TextField
					{...params}
					label="Clients"
					InputProps={{
						...params.InputProps,
						endAdornment: (
							<>
								{clientsLoading ? <CircularProgress color="inherit" size={20} /> : null}
								{params.InputProps.endAdornment}
							</>
						),
					}}
				/>
			)}
		/>
	);
};

export default ClientInput;
